/**
 * Top-level Reputation Management page.
 *
 * Layout: 4 tabs — Inbox · Sources · Settings · Setup Guide
 * - Inbox: review feed with AI-reply chips + edit/post controls
 * - Sources: connected platforms (Phase 1: mock only; Google/Meta/IG ship Phase 2)
 * - Settings: brand voice, auto-reply tier rules, escalation, signature
 * - Setup Guide: step-by-step Google + Meta OAuth setup instructions
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Star, Beaker, Globe, RefreshCw, Loader2, Plus, Trash2, Send, Bot,
  Pencil, CheckCircle2, AlertTriangle, MessageSquare,
  Settings as SettingsIcon, BookOpen, Sparkles,
  TrendingUp, Bell, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = { mock: Beaker, google: Globe, facebook: Globe, instagram: Globe };

const STATUS_LABEL = {
  new: { label: 'New', tone: 'bg-blue-100 text-blue-800' },
  replied: { label: 'Replied', tone: 'bg-emerald-100 text-emerald-800' },
  awaiting_human: { label: 'Needs your reply', tone: 'bg-amber-100 text-amber-800' },
  resolved: { label: 'Resolved', tone: 'bg-slate-100 text-slate-700' },
  hidden: { label: 'Hidden', tone: 'bg-slate-100 text-slate-500' },
  reply_failed: { label: 'Reply failed', tone: 'bg-rose-100 text-rose-800' },
};

const StarRow = ({ rating }) => (
  <div className="flex items-center gap-0.5" data-testid={`star-row-${rating || 0}`}>
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`h-4 w-4 ${i <= (rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
    ))}
  </div>
);

export default function ReputationPage() {
  const [tab, setTab] = useState('inbox');
  const [sources, setSources] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dashboard, setDashboard] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState({ platform: '', status: '', minRating: '' });
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [posting, setPosting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, src, rv, al, db, st] = await Promise.all([
        axios.get(`${API}/api/reputation/platforms`),
        axios.get(`${API}/api/reputation/sources`),
        axios.get(`${API}/api/reputation/reviews?limit=100`, { params: cleanFilter(filter) }),
        axios.get(`${API}/api/reputation/alerts?only_unread=false&limit=20`),
        axios.get(`${API}/api/reputation/dashboard`),
        axios.get(`${API}/api/reputation/settings`),
      ]);
      setPlatforms(pf.data.platforms || []);
      setSources(src.data.sources || []);
      setReviews(rv.data.reviews || []);
      setAlerts(al.data.alerts || []);
      setUnreadCount(al.data.unread_count || 0);
      setDashboard(db.data || null);
      setSettings(st.data || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load Reputation data';
      toast.error(typeof msg === 'string' ? msg : 'Load failed');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.platform, filter.status, filter.minRating]);

  useEffect(() => { loadAll(); }, [loadAll]);

  function cleanFilter(f) {
    const out = {};
    if (f.platform) out.platform = f.platform;
    if (f.status) out.status = f.status;
    if (f.minRating) out.min_rating = f.minRating;
    return out;
  }

  const connectMock = async () => {
    try {
      await axios.post(`${API}/api/reputation/sources`, { platform: 'mock', profile_name: 'Demo Restaurant' });
      toast.success('Demo source connected — hit Sync to pull sample reviews.');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not connect source');
    }
  };

  const disconnectSource = async (id) => {
    if (!window.confirm('Disconnect this source? Reviews already pulled will remain.')) return;
    await axios.delete(`${API}/api/reputation/sources/${id}`);
    toast.success('Source disconnected');
    loadAll();
  };

  const syncSource = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await axios.post(`${API}/api/reputation/sources/${id}/sync`);
      toast.success(`Pulled ${data.new_reviews} new review${data.new_reviews === 1 ? '' : 's'} · AI auto-replied ${data.auto_replied}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const startEdit = (review) => { setEditingId(review.id); setEditText(review.ai_reply?.text || ''); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };

  const postReply = async (reviewId, isEditOfAi) => {
    if (!editText.trim()) { toast.error('Reply text is empty'); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/api/reputation/reviews/${reviewId}/reply`, { text: editText.trim(), edit_ai: !!isEditOfAi });
      toast.success('Reply posted to the platform');
      setEditingId(null); setEditText(''); loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post reply');
    } finally {
      setPosting(false);
    }
  };

  const markResolved = async (reviewId) => {
    await axios.patch(`${API}/api/reputation/reviews/${reviewId}`, { status: 'resolved' });
    toast.success('Marked resolved'); loadAll();
  };

  const markAlertRead = async (alertId) => {
    await axios.post(`${API}/api/reputation/alerts/${alertId}/read`); loadAll();
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/api/reputation/settings`, patch);
      setSettings(data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const stats = useMemo(() => {
    const d = dashboard || {};
    return [
      { label: 'Total reviews',  value: d.total_reviews ?? 0,        icon: MessageSquare, tone: 'text-emerald-600 bg-emerald-50' },
      { label: 'Last 30 days',   value: d.last_30_days?.count ?? 0,  icon: TrendingUp,    tone: 'text-blue-600 bg-blue-50',     suffix: d.last_30_days?.avg ? ` · avg ${d.last_30_days.avg}★` : '' },
      { label: 'AI replies',     value: `${d.ai_replies_posted ?? 0}/${d.ai_replies_generated ?? 0}`, icon: Bot, tone: 'text-violet-600 bg-violet-50' },
      { label: 'Awaiting you',   value: d.awaiting_human ?? 0,       icon: AlertTriangle, tone: 'text-amber-600 bg-amber-50' },
      { label: 'Unread alerts',  value: d.unread_alerts ?? 0,        icon: Bell,          tone: 'text-rose-600 bg-rose-50' },
    ];
  }, [dashboard]);

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="reputation-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Star className="h-7 w-7 text-amber-400 fill-amber-400" />
            Reputation<PageInfoTooltip pageKey="reputation" />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Reply to reviews from Google, Facebook, Instagram and more — AI replies safely the moment they arrive.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="reputation-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        {stats.map((s, i) => {
          const I = s.icon;
          return (
            <Card key={i} className="p-3 border-slate-200" data-testid={`reputation-stat-${i}`}>
              <div className="flex items-start gap-2.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.tone}`}>
                  <I className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</div>
                  <div className="text-base font-semibold text-slate-900 truncate">{s.value}{s.suffix || ''}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 mb-4 flex-wrap h-auto">
          <TabsTrigger value="inbox" data-testid="tab-inbox" className="gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" /> Inbox
            {unreadCount > 0 && <Badge className="bg-rose-500 hover:bg-rose-500 text-white ml-1 h-4 px-1.5 text-[10px]">{unreadCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="gap-1.5"><Send className="h-3.5 w-3.5" /> Review Requests</TabsTrigger>
          <TabsTrigger value="sources" data-testid="tab-sources" className="gap-1.5"><Globe className="h-3.5 w-3.5" /> Sources</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="gap-1.5"><SettingsIcon className="h-3.5 w-3.5" /> Settings</TabsTrigger>
          <TabsTrigger value="setup" data-testid="tab-setup" className="gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Setup Guide</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-3">
          <FilterBar filter={filter} setFilter={setFilter} />
          {loading && reviews.length === 0 ? (
            <Card className="p-12 text-center text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></Card>
          ) : reviews.length === 0 ? (
            <Card className="p-10 text-center" data-testid="reputation-empty">
              <Star className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-700">No reviews yet</p>
              <p className="text-xs text-slate-500 mt-1">Connect a source and hit Sync to pull reviews.</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setTab('sources')}>Go to Sources</Button>
            </Card>
          ) : (
            reviews.map(r => (
              <ReviewCard key={r.id} review={r}
                editing={editingId === r.id} editText={editText} setEditText={setEditText}
                onStartEdit={() => startEdit(r)} onCancelEdit={cancelEdit}
                onPost={() => postReply(r.id, true)} onResolve={() => markResolved(r.id)}
                posting={posting} />
            ))
          )}

          {alerts.filter(a => !a.read).length > 0 && (
            <Card className="p-3 mt-5 border-amber-300 bg-amber-50" data-testid="reputation-alerts">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-semibold text-amber-900">Recent alerts</span>
              </div>
              <div className="space-y-1.5">
                {alerts.filter(a => !a.read).slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-start gap-2 text-xs" data-testid={`alert-${a.id}`}>
                    <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${a.severity === 'critical' ? 'bg-rose-500' : a.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{a.title}</div>
                      <div className="text-slate-600">{a.message}</div>
                    </div>
                    <button onClick={() => markAlertRead(a.id)} className="text-slate-500 hover:text-slate-900 text-[10px]" data-testid={`alert-read-${a.id}`}>Mark read</button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sources" className="space-y-3">
          <SourcesList sources={sources} platforms={platforms} onConnectMock={connectMock}
            onDisconnect={disconnectSource} onSync={syncSource} syncingId={syncingId} />
        </TabsContent>

        <TabsContent value="requests">
          <ReviewRequestsTab />
        </TabsContent>

        <TabsContent value="settings">
          {settings && <SettingsForm settings={settings} onSave={saveSettings} />}
        </TabsContent>

        <TabsContent value="setup">
          <SetupGuide />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const FilterBar = ({ filter, setFilter }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Select value={filter.platform || 'all'} onValueChange={v => setFilter(f => ({ ...f, platform: v === 'all' ? '' : v }))}>
      <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All platforms</SelectItem>
        <SelectItem value="mock">Demo</SelectItem>
        <SelectItem value="google">Google</SelectItem>
        <SelectItem value="facebook">Facebook</SelectItem>
        <SelectItem value="instagram">Instagram</SelectItem>
      </SelectContent>
    </Select>
    <Select value={filter.status || 'all'} onValueChange={v => setFilter(f => ({ ...f, status: v === 'all' ? '' : v }))}>
      <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All statuses</SelectItem>
        <SelectItem value="awaiting_human">Needs your reply</SelectItem>
        <SelectItem value="replied">Replied</SelectItem>
        <SelectItem value="new">New</SelectItem>
        <SelectItem value="resolved">Resolved</SelectItem>
      </SelectContent>
    </Select>
    <Select value={filter.minRating || 'all'} onValueChange={v => setFilter(f => ({ ...f, minRating: v === 'all' ? '' : v }))}>
      <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-rating"><SelectValue placeholder="Min rating" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any rating</SelectItem>
        <SelectItem value="5">5★ only</SelectItem>
        <SelectItem value="4">4★+</SelectItem>
        <SelectItem value="3">3★+</SelectItem>
        <SelectItem value="1">1★+ (incl. negative)</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const ReviewCard = ({ review, editing, editText, setEditText, onStartEdit, onCancelEdit, onPost, onResolve, posting }) => {
  const r = review;
  const ai = r.ai_reply || null;
  const hr = r.human_reply || null;
  const sl = STATUS_LABEL[r.status] || STATUS_LABEL.new;
  return (
    <Card className="p-4 border-slate-200" data-testid={`review-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-semibold text-slate-600">
            {(r.author || 'A').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium text-slate-900">{r.author || 'Anonymous'}</div>
            <div className="flex items-center gap-2 mt-0.5">
              {r.rating != null && <StarRow rating={r.rating} />}
              <span className="text-[11px] text-slate-500">{r.platform}</span>
              <span className="text-[11px] text-slate-400">·</span>
              <span className="text-[11px] text-slate-500">{relTime(r.external_created_at)}</span>
            </div>
          </div>
        </div>
        <Badge className={sl.tone + ' text-[10px]'}>{sl.label}</Badge>
      </div>

      <p className="text-sm text-slate-800 leading-relaxed mb-3">{r.text}</p>

      {ai && !editing && (
        <div className={`rounded-lg border p-3 mb-2 ${ai.posted_to_platform ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`} data-testid={`ai-reply-${r.id}`}>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Bot className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-[11px] font-semibold text-violet-900">AI reply</span>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-violet-300 text-violet-700">
                {Math.round((ai.confidence || 0) * 100)}% conf
              </Badge>
              {ai.posted_to_platform ? (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-emerald-300 text-emerald-700 gap-1">
                  <CheckCircle2 className="h-2.5 w-2.5" /> posted
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-amber-300 text-amber-700">awaiting your approval</Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={onStartEdit} data-testid={`edit-ai-${r.id}`}>
              <Pencil className="h-3 w-3" /> Edit
            </Button>
          </div>
          <p className="text-[13px] text-slate-700 italic">&ldquo;{ai.text}&rdquo;</p>
        </div>
      )}

      {hr && !editing && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 mb-2" data-testid={`human-reply-${r.id}`}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[11px] font-semibold text-blue-900">{hr.edited_ai ? 'Edited & posted by you' : 'Posted by you'}</span>
            <span className="text-[10px] text-blue-700">{relTime(hr.posted_at)}</span>
          </div>
          <p className="text-[13px] text-slate-700">{hr.text}</p>
        </div>
      )}

      {editing && (
        <div className="rounded-lg border-2 border-emerald-300 bg-white p-3 space-y-2">
          <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={3}
            className="text-sm" data-testid={`reply-textarea-${r.id}`}
            placeholder="Type your reply…" maxLength={2000} />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-500">{editText.length}/2000</span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="ghost" onClick={onCancelEdit} className="h-7 text-xs" data-testid={`cancel-${r.id}`}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 gap-1"
                disabled={posting || !editText.trim()} onClick={onPost} data-testid={`post-${r.id}`}>
                {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                Post reply
              </Button>
            </div>
          </div>
        </div>
      )}

      {!editing && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-2">
          {r.status !== 'replied' && !ai && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={onStartEdit} data-testid={`reply-${r.id}`}>
              <MessageSquare className="h-3 w-3" /> Reply
            </Button>
          )}
          {r.status !== 'resolved' && (
            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-slate-500" onClick={onResolve} data-testid={`resolve-${r.id}`}>
              <CheckCircle2 className="h-3 w-3" /> Mark resolved
            </Button>
          )}
          {r.permalink && (
            <a href={r.permalink} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-slate-900 ml-auto flex items-center gap-1">
              View on platform <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </Card>
  );
};

const SourcesList = ({ sources, onConnectMock, onDisconnect, onSync, syncingId }) => (
  <div className="space-y-3" data-testid="reputation-sources">
    <Card className="p-4 border-dashed border-emerald-300 bg-emerald-50/40">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Beaker className="h-4 w-4 text-emerald-600" /> Demo source</div>
          <div className="text-xs text-slate-600 mt-1">Test the full Reputation pipeline (sync → AI auto-reply → alerts) with synthetic reviews.</div>
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 gap-1" onClick={onConnectMock} data-testid="connect-mock-btn">
          <Plus className="h-3.5 w-3.5" /> Connect demo
        </Button>
      </div>
    </Card>

    {sources.length === 0 ? (
      <Card className="p-8 text-center text-sm text-slate-500">No sources connected yet.</Card>
    ) : (
      sources.map(s => {
        const I = PLATFORM_ICONS[s.platform] || Globe;
        return (
          <Card key={s.id} className="p-4 flex items-center gap-3" data-testid={`source-${s.id}`}>
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><I className="h-5 w-5 text-slate-700" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">{s.profile_name}</div>
              <div className="text-[11px] text-slate-500">{s.platform} · {s.status} · {s.last_sync_at ? `last sync ${relTime(s.last_sync_at)}` : 'never synced'}</div>
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => onSync(s.id)}
              disabled={syncingId === s.id} data-testid={`sync-${s.id}`}>
              {syncingId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Sync
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs text-rose-500 hover:bg-rose-50"
              onClick={() => onDisconnect(s.id)} data-testid={`disconnect-${s.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </Card>
        );
      })
    )}

    <Card className="p-4 mt-5 border-slate-200">
      <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-violet-500" /> Coming in Phase 2 (provide credentials → I'll wire the OAuth flows)
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {['Google Business Profile', 'Facebook Pages', 'Instagram Business', 'Yelp', 'TripAdvisor', 'Trustpilot'].map((name, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
            <Globe className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-700">{name}</span>
            <Badge variant="outline" className="ml-auto text-[9px] border-slate-300 text-slate-500">soon</Badge>
          </div>
        ))}
      </div>
    </Card>
  </div>
);

const SettingsForm = ({ settings, onSave }) => {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  return (
    <Card className="p-5 max-w-2xl space-y-4" data-testid="reputation-settings-form">
      <div>
        <Label className="text-xs">Brand voice</Label>
        <Select value={s.brand_voice} onValueChange={v => set('brand_voice', v)}>
          <SelectTrigger className="h-9 text-sm mt-1.5" data-testid="settings-voice"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="warm_professional">Warm & professional</SelectItem>
            <SelectItem value="brief_courteous">Brief & courteous</SelectItem>
            <SelectItem value="playful">Friendly & playful</SelectItem>
            <SelectItem value="formal">Formal & respectful</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500 mt-1">Used to shape every AI auto-reply.</p>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div>
          <Label className="text-xs">Auto-reply enabled</Label>
          <p className="text-[11px] text-slate-500">When off, every reply waits for your approval.</p>
        </div>
        <Switch checked={s.auto_reply_enabled} onCheckedChange={v => set('auto_reply_enabled', v)} data-testid="settings-auto-toggle" />
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
        <div>
          <Label className="text-xs">Auto-reply minimum rating</Label>
          <Input type="number" min={1} max={5} value={s.auto_reply_min_rating}
            onChange={e => set('auto_reply_min_rating', parseInt(e.target.value, 10) || 1)} className="h-9 mt-1.5" data-testid="settings-min" />
        </div>
        <div>
          <Label className="text-xs">Human required below</Label>
          <Input type="number" min={1} max={5} value={s.human_required_below_rating}
            onChange={e => set('human_required_below_rating', parseInt(e.target.value, 10) || 3)} className="h-9 mt-1.5" data-testid="settings-human-threshold" />
        </div>
      </div>
      <p className="text-[11px] text-slate-500 -mt-2">Reviews below this rating get an AI draft but wait for human approval before posting.</p>

      <div className="border-t border-slate-100 pt-4">
        <Label className="text-xs">Reply signature (optional)</Label>
        <Input value={s.signature || ''} placeholder="e.g. — The Acme Team"
          onChange={e => set('signature', e.target.value)} className="h-9 mt-1.5" data-testid="settings-signature" />
      </div>

      <div>
        <Label className="text-xs">Alert email</Label>
        <Input value={s.alert_email || ''} placeholder="admin@yourbusiness.com"
          onChange={e => set('alert_email', e.target.value)} className="h-9 mt-1.5" data-testid="settings-alert-email" />
        <p className="text-[11px] text-slate-500 mt-1">Critical alerts (1-2★ reviews) are emailed here.</p>
      </div>

      <div className="flex justify-end pt-3 border-t border-slate-100">
        <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={() => onSave(s)} data-testid="settings-save">Save</Button>
      </div>
    </Card>
  );
};

const SetupGuide = () => (
  <div className="space-y-4 max-w-3xl text-sm text-slate-700" data-testid="reputation-setup-guide">
    <Card className="p-5">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <Globe className="h-4 w-4 text-blue-500" /> Step 1: Set up Google Business Profile
      </h2>
      <ol className="list-decimal pl-5 space-y-1.5 text-[13px]">
        <li>Go to <a className="text-emerald-600 underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a> → create a new project (e.g. "Konnect Reputation").</li>
        <li>Enable these APIs (search in the API library): <strong>My Business Account Management API</strong>, <strong>My Business Business Information API</strong>, <strong>Google Business Profile API</strong>.</li>
        <li>OAuth consent screen → choose <strong>External</strong>, add your business email as a test user, fill app info.</li>
        <li>Credentials → Create OAuth 2.0 Client ID → <strong>Web application</strong> → Authorized redirect URI: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[12px]">https://konnect.kaharian.com/api/reputation/oauth/google/callback</code></li>
        <li>Copy the <strong>Client ID</strong> + <strong>Client Secret</strong> → paste them into Konnect Settings → Integrations (Phase 2 will activate this UI).</li>
        <li>Apply for production verification (one-time, ~3 business days, only needed before non-test users authenticate).</li>
      </ol>
    </Card>

    <Card className="p-5">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <Globe className="h-4 w-4 text-blue-700" /> Step 2: Set up Meta (Facebook + Instagram)
      </h2>
      <ol className="list-decimal pl-5 space-y-1.5 text-[13px]">
        <li>Go to <a className="text-emerald-600 underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">Meta for Developers → My Apps</a> → Create App → choose <strong>Business</strong> type.</li>
        <li>Add products: <strong>Facebook Login</strong> + <strong>Instagram Graph API</strong>.</li>
        <li>App settings → Basic → copy <strong>App ID</strong> and <strong>App Secret</strong>.</li>
        <li>Facebook Login → Settings → Valid OAuth Redirect URIs: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[12px]">https://konnect.kaharian.com/api/reputation/oauth/meta/callback</code></li>
        <li>App Review → request these permissions: <code>pages_show_list</code>, <code>pages_read_engagement</code>, <code>pages_manage_engagement</code>, <code>pages_manage_metadata</code>, <code>instagram_basic</code>, <code>instagram_manage_comments</code>.</li>
        <li>Submit a 60-90 second screencast showing how Konnect uses each permission (Meta requires this).</li>
        <li>Once approved, paste App ID + Secret into Konnect Settings → Integrations (Phase 2 will activate this UI).</li>
      </ol>
    </Card>

    <Card className="p-5">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <Plus className="h-4 w-4 text-violet-500" /> Step 3: Want a platform we don't yet support?
      </h2>
      <p className="text-[13px] mb-2">The connector framework is fully pluggable. Drop a new <code>connectors/&lt;name&gt;.py</code> with three methods (<code>fetch_reviews</code>, <code>post_reply</code>, optional <code>health_check</code>) and register it in <code>connectors/__init__.py</code>. The frontend, AI auto-reply, alerts, and dashboard pick it up automatically.</p>
      <p className="text-[13px] text-slate-500">Examples we can wire next: TripAdvisor, Yelp, Trustpilot, Glassdoor, Zomato, Booking.com, Etsy, Shopee.</p>
    </Card>
  </div>
);

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso); const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

// ─── Review Requests tab ────────────────────────────────────────────────
function ReviewRequestsTab() {
  const [links, setLinks] = useState({ google: '', facebook: '', instagram: '', trustpilot: '', custom: '' });
  const [savingLinks, setSavingLinks] = useState(false);
  const [requests, setRequests] = useState([]);
  const [funnel, setFunnel] = useState({});
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', order_reference: '', channel: 'auto', custom_message: '' });
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [l, r, f] = await Promise.all([
        axios.get(`${API}/api/reputation/review-requests/links`),
        axios.get(`${API}/api/reputation/review-requests`),
        axios.get(`${API}/api/reputation/review-requests/funnel`),
      ]);
      setLinks({
        google: l.data.google || '', facebook: l.data.facebook || '',
        instagram: l.data.instagram || '', trustpilot: l.data.trustpilot || '',
        custom: l.data.custom || '',
      });
      setRequests(r.data.requests || []);
      setFunnel(f.data.funnel || {});
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const saveLinks = async () => {
    setSavingLinks(true);
    try {
      await axios.put(`${API}/api/reputation/review-requests/links`, links);
      toast.success('Review links saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingLinks(false); }
  };

  const send = async () => {
    if (!form.customer_name.trim() || (!form.customer_email && !form.customer_phone)) {
      toast.error('Need a name and at least an email or phone'); return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/reputation/review-requests/send`, form);
      toast.success(`Sent via ${(data.sent_via || []).join(', ') || 'queue'}`);
      setForm({ customer_name: '', customer_email: '', customer_phone: '', order_reference: '', channel: 'auto', custom_message: '' });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="review-requests-tab">
      {/* Funnel cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { k: 'queued',   l: 'Queued',    color: 'bg-slate-100 text-slate-700' },
          { k: 'sent',     l: 'Sent',      color: 'bg-blue-100 text-blue-800' },
          { k: 'opened',   l: 'Opened',    color: 'bg-violet-100 text-violet-800' },
          { k: 'clicked',  l: 'Clicked',   color: 'bg-amber-100 text-amber-800' },
          { k: 'completed',l: 'Completed', color: 'bg-emerald-100 text-emerald-800' },
        ].map(({ k, l, color }) => (
          <div key={k} className="p-3 bg-white rounded-lg border border-slate-200" data-testid={`funnel-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
            <div className="text-lg font-semibold mt-0.5">
              <span className={`inline-block px-2 rounded-md ${color}`}>{funnel[k] || 0}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Configure review destinations */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-1.5">
          <ExternalLink className="h-4 w-4 text-emerald-500" /> Where should customers leave reviews?
        </h3>
        <p className="text-xs text-slate-500 mb-3">Paste your direct review URLs. Leave blank to hide a platform from the smart-link page.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['google', 'Google Business Profile', 'https://g.page/r/...'],
            ['facebook', 'Facebook Page reviews', 'https://www.facebook.com/yourpage/reviews'],
            ['instagram', 'Instagram profile', 'https://www.instagram.com/yourbusiness'],
            ['trustpilot', 'Trustpilot', 'https://www.trustpilot.com/review/yourdomain.com'],
            ['custom', 'Other (custom)', 'https://...'],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <Label className="text-xs">{label}</Label>
              <Input className="h-9 mt-1.5" placeholder={ph} value={links[k]}
                     onChange={e => setLinks(l => ({ ...l, [k]: e.target.value }))}
                     data-testid={`link-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={saveLinks}
                  disabled={savingLinks} data-testid="save-links">
            {savingLinks ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save links
          </Button>
        </div>
      </Card>

      {/* Send a request */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
          <Send className="h-4 w-4 text-emerald-500" /> Send a review request
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Customer name *</Label>
            <Input className="h-9 mt-1.5" value={form.customer_name}
                   onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                   data-testid="rr-name" />
          </div>
          <div>
            <Label className="text-xs">Order reference (optional)</Label>
            <Input className="h-9 mt-1.5" value={form.order_reference}
                   onChange={e => setForm(f => ({ ...f, order_reference: e.target.value }))}
                   data-testid="rr-ref" />
          </div>
          <div>
            <Label className="text-xs">Email (for Resend email)</Label>
            <Input type="email" className="h-9 mt-1.5" value={form.customer_email}
                   onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                   data-testid="rr-email" />
          </div>
          <div>
            <Label className="text-xs">Phone E.164 (for Twilio SMS)</Label>
            <Input className="h-9 mt-1.5" placeholder="+639..." value={form.customer_phone}
                   onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                   data-testid="rr-phone" />
          </div>
          <div>
            <Label className="text-xs">Channel</Label>
            <Select value={form.channel} onValueChange={v => setForm(f => ({ ...f, channel: v }))}>
              <SelectTrigger className="h-9 mt-1.5" data-testid="rr-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (email if available, else SMS)</SelectItem>
                <SelectItem value="email">Email only</SelectItem>
                <SelectItem value="sms">SMS only</SelectItem>
                <SelectItem value="both">Both email + SMS</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Custom message (optional)</Label>
          <Textarea className="mt-1.5 text-xs" rows={2} maxLength={400}
                    placeholder="Leave blank for the default friendly ask…"
                    value={form.custom_message}
                    onChange={e => setForm(f => ({ ...f, custom_message: e.target.value }))}
                    data-testid="rr-message" />
        </div>
        <div className="flex justify-end mt-3">
          <Button className="bg-emerald-600 hover:bg-emerald-500 gap-1" onClick={send}
                  disabled={sending || !form.customer_name.trim()} data-testid="rr-send">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send request
          </Button>
        </div>
      </Card>

      {/* Recent requests */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Recent requests ({requests.length})</h3>
        {requests.length === 0 ? (
          <p className="text-xs text-slate-500">No requests sent yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {requests.slice(0, 20).map(r => (
              <div key={r.id} className="py-2 flex items-center gap-3 text-xs" data-testid={`rr-row-${r.id}`}>
                <span className={`px-1.5 rounded text-[10px] ${
                  r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                  r.status === 'clicked' ? 'bg-amber-100 text-amber-700' :
                  r.status === 'opened' ? 'bg-violet-100 text-violet-700' :
                  r.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-700'
                }`}>{r.status}</span>
                <span className="font-medium text-slate-800">{r.customer_name}</span>
                <span className="text-slate-500">{r.customer_email || r.customer_phone}</span>
                <span className="text-slate-400 ml-auto">{(r.sent_via || []).join(', ')}</span>
                <span className="text-slate-400">{relTime(r.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
